import { useState, useRef, useEffect } from 'react';
import { Column } from './Column';
import './TimePicker.css';

interface TimeValue {
  hour: string; // 00–23
  minute: string;
}

interface TimePickerProps {
  value?: string; // "HH:mm" 24-hour
  onChange: (time: string) => void;
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
  id?: string;
  name?: string;
  min?: string; // "HH:mm"
  max?: string; // "HH:mm"
  /**
   * `pill` — single-row trigger matching SingleDatePicker (3.5rem, #F2F2F7).
   * `default` — stacked label + value (e.g. event time intervals).
   */
  layout?: 'default' | 'pill';
  /** Open the list above the trigger (e.g. bottom of a modal). Default opens downward. */
  dropdownPlacement?: 'bottom' | 'top';
}

const HOURS = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
const MINUTES = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));

function roundMinuteToStep(m: number, step = 5): number {
  return Math.round(m / step) * step;
}

function currentTimeValue(): TimeValue {
  const now = new Date();
  return {
    hour: now.getHours().toString().padStart(2, '0'),
    minute: roundMinuteToStep(now.getMinutes()).toString().padStart(2, '0'),
  };
}

function timeToInternal(time: string): TimeValue {
  const [hStr, mStr] = time.split(':');
  const h = Math.min(23, Math.max(0, parseInt(hStr ?? '0', 10)));
  const m = Math.min(55, Math.max(0, roundMinuteToStep(parseInt(mStr ?? '0', 10))));
  return {
    hour: h.toString().padStart(2, '0'),
    minute: m.toString().padStart(2, '0'),
  };
}

function internalToTime(tv: TimeValue): string {
  return `${tv.hour}:${tv.minute}`;
}

function parseMinutes(time: string | undefined): number | null {
  if (!time) return null;
  const [h, m] = time.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return null;
  return h * 60 + m;
}

function clampTime(time24: string, min?: string, max?: string): string {
  const t = parseMinutes(time24);
  if (t === null) return time24;
  const minM = parseMinutes(min);
  const maxM = parseMinutes(max);
  let clamped = t;
  if (minM !== null && clamped < minM) clamped = minM;
  if (maxM !== null && clamped > maxM) clamped = maxM;
  if (clamped === t) return time24;
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export const TimePicker = ({
  value,
  onChange,
  placeholder = 'Time',
  error = false,
  disabled = false,
  id,
  name,
  min,
  max,
  layout = 'default',
  dropdownPlacement = 'bottom',
}: TimePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [internal, setInternal] = useState<TimeValue>(
    value ? timeToInternal(value) : currentTimeValue(),
  );

  useEffect(() => {
    if (value) setInternal(timeToInternal(value));
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const narrow =
        typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
      dropdownRef.current.scrollIntoView({
        behavior: 'smooth',
        block: dropdownPlacement === 'top' && narrow ? 'center' : 'nearest',
        inline: 'nearest',
      });
    }
  }, [isOpen, dropdownPlacement]);

  const handleSelect = (type: keyof TimeValue, val: string) => {
    const next = { ...internal, [type]: val };
    setInternal(next);
    onChange(clampTime(internalToTime(next), min, max));
  };

  const displayTime = value ?? null;

  const triggerClass = [
    'tp-trigger',
    layout === 'pill' ? 'tp-trigger--pill' : '',
    dropdownPlacement === 'top' ? 'tp-trigger--dropdown-top' : '',
    isOpen && !error ? 'tp-trigger--open' : '',
    error ? 'tp-trigger--error' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div style={{ position: 'relative', width: '100%' }} ref={containerRef}>
      {id && (
        <input
          type="text"
          id={id}
          name={name}
          value={value || ''}
          style={{ display: 'none' }}
          readOnly
          aria-hidden="true"
          tabIndex={-1}
        />
      )}

      <button
        type="button"
        className={triggerClass}
        onClick={() => { if (!disabled) setIsOpen((p) => !p); }}
        onKeyDown={(e) => { if (e.key === 'Escape') setIsOpen(false); }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        disabled={disabled}
      >
        {layout === 'default' && (
          <span className="tp-trigger__label">{placeholder}</span>
        )}
        <div className="tp-trigger__value-row">
          <span
            className={`tp-trigger__value${displayTime ? '' : ' tp-trigger__value--empty'}${
              layout === 'pill' ? ' tp-trigger__value--pill' : ''
            }`}
            style={
              layout === 'pill'
                ? { color: displayTime ? '#000' : '#8E8E93' }
                : undefined
            }
          >
            {layout === 'pill' ? (displayTime ?? placeholder) : (displayTime ?? '--:--')}
          </span>
          <span className="tp-trigger__chevron-wrap">
            <svg
              className={`tp-trigger__chevron${isOpen ? ' tp-trigger__chevron--open' : ''}${
                layout === 'pill' ? ' tp-trigger__chevron--pill' : ''
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </span>
        </div>
      </button>

      {isOpen && !disabled && (
        <div
          className={`tp-dropdown${dropdownPlacement === 'top' ? ' tp-dropdown--placement-top' : ''}`}
          ref={dropdownRef}
        >
          <div className="tp-dropdown__header">
            <span className="tp-dropdown__header-text">Select Time</span>
          </div>

          <div className="tp-dropdown__grid">
            <div className="tp-dropdown__pill" />

            <Column
              items={HOURS}
              selected={internal.hour}
              onSelect={(v) => handleSelect('hour', v)}
              isOpen={isOpen}
            />
            <Column
              items={MINUTES}
              selected={internal.minute}
              onSelect={(v) => handleSelect('minute', v)}
              isOpen={isOpen}
            />
          </div>
        </div>
      )}
    </div>
  );
};
